import { cn } from "./cn";

/**
 * DERS BAZLI HEDEF FARKI — F-EXM-09 (Emre kararı 07.10.2026)
 *
 * Son denemenin her dersi için net, hedef ve fark. Fark HESAPLANMAZ: sunucu
 * `fark = net − hedef` olarak verir (`DenemeDersHedefFarki`); ekran yalnız yazar.
 * `ui` paketi contracts'a bağımlı değil (sunum katmanı), bu yüzden tip yapısal olarak
 * `DenemeDersHedefFarki` ile AYNI alanları taşır ve ona doğrudan verilebilir.
 *
 * Renk tek başına anlam taşımaz: her satırda "hedefin altında/üstünde/hedefte" yazar.
 * Ölçülmemiş ders "0 net" DEĞİL, "Ölçülmedi" (0 net ile ölçülmemiş ayrı şeylerdir).
 */
export interface DersHedefSatiri {
  lessonId: string;
  ders: string;
  /** Ölçülmediyse `null`. */
  net: number | null;
  hedef: number;
  /** `net − hedef`; net yoksa `null`. */
  fark: number | null;
}

function yuvarla(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Türkçe sayı, en az 1 ondalık: 2,5 · 12,0 · 72,25. */
export function netMetni(n: number): string {
  return new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  }).format(yuvarla(n));
}

/** İşaretli fark: "+2,5" · "−1,0" · "0,0" (eksi işareti U+2212). */
export function isaretliFark(n: number): string {
  const y = yuvarla(n);
  if (y > 0) return `+${netMetni(y)}`;
  if (y < 0) return `−${netMetni(Math.abs(y))}`;
  return netMetni(0);
}

export function farkYonu(n: number): "ust" | "alt" | "hedefte" {
  const y = yuvarla(n);
  return y > 0 ? "ust" : y < 0 ? "alt" : "hedefte";
}

const YON_METNI = {
  ust: "hedefin üstünde",
  alt: "hedefin altında",
  hedefte: "hedefte",
} as const;

const YON_RENGI = {
  ust: "text-onay-700",
  alt: "text-hata-700",
  hedefte: "text-notr-700",
} as const;

export function DersHedefFarki({
  dersler,
  baslik = "Ders bazlı hedef farkı",
  aciklama,
}: {
  dersler: readonly DersHedefSatiri[];
  baslik?: string;
  /** Örneğin son denemenin adı. */
  aciklama?: string;
}) {
  if (dersler.length === 0) return null;
  return (
    <section aria-label={baslik} className="flex flex-col gap-1.5">
      <h3 className="text-notr-900 text-sm font-semibold">{baslik}</h3>
      {aciklama && <p className="text-notr-600 text-xs">{aciklama}</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <caption className="sr-only">{baslik}</caption>
          <thead>
            <tr className="text-notr-600 border-notr-200 border-b text-xs">
              <th scope="col" className="py-1.5 pr-2 font-medium">
                Ders
              </th>
              <th scope="col" className="px-2 py-1.5 text-right font-medium">
                Net
              </th>
              <th scope="col" className="px-2 py-1.5 text-right font-medium">
                Hedef
              </th>
              <th scope="col" className="py-1.5 pl-2 text-right font-medium">
                Fark
              </th>
            </tr>
          </thead>
          <tbody>
            {dersler.map((d) => {
              const yon = d.fark === null ? null : farkYonu(d.fark);
              return (
                <tr key={d.lessonId} className="border-notr-100 border-b last:border-b-0">
                  <th scope="row" className="text-notr-900 py-1.5 pr-2 font-medium">
                    {d.ders}
                  </th>
                  <td className="text-notr-900 px-2 py-1.5 text-right tabular-nums">
                    {d.net === null ? (
                      <span className="text-notr-600">Ölçülmedi</span>
                    ) : (
                      netMetni(d.net)
                    )}
                  </td>
                  <td className="text-notr-700 px-2 py-1.5 text-right tabular-nums">
                    {netMetni(d.hedef)}
                  </td>
                  <td className="py-1.5 pl-2 text-right">
                    {d.fark === null || yon === null ? (
                      <span className="text-notr-600">—</span>
                    ) : (
                      <span className={cn("font-medium", YON_RENGI[yon])}>
                        <span className="tabular-nums">{isaretliFark(d.fark)}</span>
                        <span className="block text-xs font-normal">{YON_METNI[yon]}</span>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
